import React, { FC, Fragment, useEffect, useState } from 'react';
import { Alert, Autocomplete, Box, Button, Divider, Grid, TextField, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useLazyQuery, useQuery } from '@apollo/client';
import { Organization } from 'models/organization';
import { User } from 'models/user';
import { ClientType, LabClient } from 'models/client-directory';
import { useLabClients } from 'hooks/useLabClients';
import { getTechniciansForUserListTemplate } from 'apollo/templates/user/query.graphql';
import { useDenthubErrorBoundary } from 'components/shared/denthub-error-boundary/denthub-error-boundary-context';
import Loader from 'components/shared/loader/loader.component';
import CustomerGridItem from 'components/order-flow/shared/customer-grid-item/customer-grid-item.component';
import { DEFAULT_MESSAGE_FIELDS } from 'components/order-flow/subcontract-order/communication-step/subcontract-message';
import { useSubcontractOrder } from 'pages/order-flow/subcontract-order/subcontract-order-context';
import { subcontractDefaultsTemplate } from 'apollo/templates/subcontract/subcontract.graphql';
import { selectionFromDefaults } from 'components/order-flow/subcontract-order/subcontract-input-helper';
import { currentOrgVar } from 'apollo/reactive-variables/organization';

type SubcontractorStepProps = {
    onNextClick: () => void;
};

/**
 * DD-3484: pick the subcontractor — any connected partner lab, chosen at this moment —
 * and the responsible user at the subcontracting lab. DD-3485: choosing a partner
 * pre-fills the content selection from this lab's defaults for it; every item is still
 * shown and confirmed in the following steps.
 */
const SubcontractorStep: FC<SubcontractorStepProps> = ({ onNextClick }) => {
    const { t: translate } = useTranslation(['orders', 'common']);
    const { parentOrder, subcontract, updateSubcontract } = useSubcontractOrder();
    const [labClients, setLabClients] = useState<LabClient[]>([]);
    const [labUsers, setLabUsers] = useState<User[]>([]);
    const { showBoundary } = useDenthubErrorBoundary();

    const { getLabClients, isLabClientListLoading } = useLabClients(setLabClients, ClientType.SUPPLIER);

    const [fetchPartnerDefaults, { loading: isPartnerDefaultsLoading }] = useLazyQuery(subcontractDefaultsTemplate, {
        fetchPolicy: 'no-cache',
        onCompleted: ({ supplier }) => {
            const defaults = supplier?.subcontractDefaults;
            const allowedResponsibleUserIds: string[] = defaults?.responsibleUserIds ?? [];
            const isResponsibleUserAllowed =
                !allowedResponsibleUserIds.length ||
                allowedResponsibleUserIds.includes(subcontract.responsibleUser?._id ?? '');
            updateSubcontract({
                selection: selectionFromDefaults(parentOrder, defaults),
                // DD-3689: the partner's message parts (none = a blank message) and language.
                messageFields: defaults?.messageFields ?? DEFAULT_MESSAGE_FIELDS,
                messageLanguage: defaults?.messageLanguage ?? null,
                // DD-3689: the partner's responsible users; one is pre-filled.
                allowedResponsibleUserIds,
                ...(isResponsibleUserAllowed ? {} : { responsibleUser: undefined }),
            });
        },
        onError(error) {
            showBoundary(error);
        },
    });

    const selectPartner = (lab: Organization) => {
        if (lab._id === subcontract.supplier?._id) return;
        updateSubcontract({ supplier: lab });
        fetchPartnerDefaults({ variables: { supplierId: lab._id } });
    };

    useEffect(() => {
        getLabClients({ variables: { offset: 0, limit: 0 } });
    }, []);

    const { loading: isLabUserListLoading } = useQuery(getTechniciansForUserListTemplate, {
        variables: {
            filters: { active: true, role: 'technician' },
            limit: 0,
            offset: 0,
        },
        onCompleted: ({ technicians }) => setLabUsers(technicians),
        onError(error) {
            showBoundary(error);
        },
    });

    // DD-3689: a partner with exactly one responsible user gets them pre-filled.
    const [onlyAllowedUserId] =
        subcontract.allowedResponsibleUserIds.length === 1 ? subcontract.allowedResponsibleUserIds : [];
    useEffect(() => {
        if (!onlyAllowedUserId || subcontract.responsibleUser?._id === onlyAllowedUserId) return;
        const onlyAllowedUser = labUsers.find((user) => user._id === onlyAllowedUserId);
        if (onlyAllowedUser) updateSubcontract({ responsibleUser: onlyAllowedUser });
    }, [onlyAllowedUserId, labUsers]);

    const responsibleUserOptions = subcontract.allowedResponsibleUserIds.length
        ? labUsers.filter((user) => subcontract.allowedResponsibleUserIds.includes(user._id))
        : labUsers;

    if (isLabClientListLoading) {
        return <Loader />;
    }

    // A lab can hold a client connection to itself; it is never a subcontractor (the
    // backend refuses it too).
    const ownOrganizationId = currentOrgVar()?._id;
    const partners = labClients.filter((labClient) => labClient.lab?._id !== ownOrganizationId);

    const canProceed = Boolean(
        subcontract.supplier?._id && subcontract.responsibleUser?._id && !isPartnerDefaultsLoading,
    );

    return (
        <Fragment>
            <Box mb={2}>
                <Typography variant="h1">{translate('orders:SUBCONTRACTOR')}</Typography>
            </Box>
            <Box mb={2}>
                <Divider />
            </Box>
            {partners.length ? (
                <Box mb={4}>
                    <Box mb={4} maxWidth={360}>
                        <Box mb={1}>
                            <Typography variant="h2">{translate('orders:RESPONSIBLE_USER')}</Typography>
                        </Box>
                        <Autocomplete
                            options={responsibleUserOptions}
                            value={subcontract.responsibleUser || null}
                            loading={isLabUserListLoading}
                            fullWidth
                            onChange={(_event, selectedUser) =>
                                updateSubcontract({ responsibleUser: selectedUser || undefined })
                            }
                            isOptionEqualToValue={(option, value) => option?._id === value?._id}
                            getOptionLabel={(option) => option.fullName || ''}
                            renderInput={(inputParams) => (
                                <TextField
                                    {...inputParams}
                                    label={translate('orders:RESPONSIBLE_USER')}
                                    required
                                    fullWidth
                                    data-test="subcontractor-step.responsible-user"
                                />
                            )}
                        />
                    </Box>
                    <Box mb={2}>
                        <Typography variant="h2">{translate('orders:CHOOSE_SUPPLIER')}</Typography>
                    </Box>
                    <Grid container spacing={2} alignItems="stretch">
                        {partners.map((labClient) => (
                            <CustomerGridItem
                                key={labClient._id}
                                lab={labClient.lab as Organization}
                                isSelected={subcontract.supplier?._id === labClient.lab._id}
                                handleLabSelect={selectPartner}
                            />
                        ))}
                    </Grid>
                </Box>
            ) : (
                <Box mb={3}>
                    <Alert severity="info">{translate('orders:NO_SUPPLIER_INFO')}</Alert>
                </Box>
            )}
            <Box display="flex" justifyContent="flex-end">
                <Button
                    variant="contained"
                    disabled={!canProceed}
                    onClick={onNextClick}
                    data-test="subcontractor-step.next-btn"
                >
                    {translate('orders:NEXT_STEP')}
                </Button>
            </Box>
        </Fragment>
    );
};

export default SubcontractorStep;
